import React from 'react';
import {
  Card,
  CardContent,
  Chip,
  Box,
  Typography,
  Popover,
  Button,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Cancel,
  TrendingUp,
  ShowChart,
  TrendingDown,
  HelpOutline,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  ReadinessStatus,
  ConfidenceLevel,
  ReadinessConfig,
  ConfidenceConfig,
  TrustIndicatorProps,
} from '../../types/readinessConfidence.types';

const READINESS_CONFIG: Record<ReadinessStatus, ReadinessConfig & { icon: React.ReactElement }> = {
  PASS: {
    label: 'PASS',
    color: 'success',
    description: 'All SEO validation checks passed',
    icon: <CheckCircle />,
  },
  WARN: {
    label: 'WARN',
    color: 'warning',
    description: 'Some validation warnings detected',
    icon: <Warning />,
  },
  FAIL: {
    label: 'FAIL',
    color: 'error',
    description: 'Critical validation failures found',
    icon: <Cancel />,
  },
};

const CONFIDENCE_CONFIG: Record<ConfidenceLevel, ConfidenceConfig & { icon: React.ReactElement }> = {
  HIGH: {
    label: 'HIGH',
    color: 'success',
    description: 'Strong statistical evidence (p<0.01, n≥28)',
    icon: <TrendingUp />,
  },
  MEDIUM: {
    label: 'MEDIUM',
    color: 'info',
    description: 'Moderate evidence (p<0.05, n≥14)',
    icon: <ShowChart />,
  },
  LOW: {
    label: 'LOW',
    color: 'warning',
    description: 'Limited statistical evidence',
    icon: <TrendingDown />,
  },
  INSUFFICIENT: {
    label: 'INSUFFICIENT',
    color: 'default',
    description: 'Not enough data to evaluate',
    icon: <HelpOutline />,
  },
};

const getTrustInterpretation = (readiness: ReadinessStatus, confidence: ConfidenceLevel): string => {
  if (readiness === 'PASS' && confidence === 'HIGH') {
    return '✓ High trust: Technically sound with strong statistical support';
  }
  if (readiness === 'PASS' && confidence === 'MEDIUM') {
    return '✓ Moderate trust: Technically sound but limited data';
  }
  if (readiness === 'PASS' && confidence === 'LOW') {
    return '⚠ Low trust: Technically sound but insufficient sample size';
  }
  if (readiness === 'WARN' && confidence === 'HIGH') {
    return '⚠ Mixed trust: Strong data but technical issues detected';
  }
  if (readiness === 'WARN' && confidence === 'MEDIUM') {
    return '⚠ Caution: Technical warnings and moderate data quality';
  }
  if (readiness === 'FAIL') {
    return '✗ Do not trust: Critical validation failures';
  }
  if (confidence === 'INSUFFICIENT') {
    return '⚠ Insufficient data: Cannot evaluate statistical confidence';
  }
  return '⚠ Low trust: Consider improving both readiness and data quality';
};

export default function TrustIndicator({
  readiness,
  confidence,
  readinessReason,
  confidenceReason,
  readinessEvidenceLink,
  confidenceEvidenceLink,
  interpretation,
  size = 'medium',
  orientation = 'horizontal',
}: TrustIndicatorProps) {
  const [readinessAnchorEl, setReadinessAnchorEl] = React.useState<HTMLElement | null>(null);
  const [confidenceAnchorEl, setConfidenceAnchorEl] = React.useState<HTMLElement | null>(null);

  const readinessConfig = READINESS_CONFIG[readiness];
  const confidenceConfig = CONFIDENCE_CONFIG[confidence];

  const handleReadinessClick = (event: React.MouseEvent<HTMLElement>) => {
    setReadinessAnchorEl(event.currentTarget);
  };

  const handleConfidenceClick = (event: React.MouseEvent<HTMLElement>) => {
    setConfidenceAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setReadinessAnchorEl(null);
    setConfidenceAnchorEl(null);
  };

  const displayInterpretation = interpretation || getTrustInterpretation(readiness, confidence);

  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Stack
          direction={orientation === 'horizontal' ? 'row' : 'column'}
          spacing={2}
          alignItems="center"
        >
          {/* Readiness Badge */}
          <Chip
            label={`Readiness: ${readinessConfig.label}`}
            color={readinessConfig.color}
            size={size}
            icon={readinessConfig.icon as any}
            onClick={handleReadinessClick}
            sx={{ cursor: 'help', fontWeight: 600 }}
          />

          {orientation === 'horizontal' && (
            <Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>
              ×
            </Typography>
          )}

          {/* Confidence Badge */}
          <Chip
            label={`Confidence: ${confidenceConfig.label}`}
            color={confidenceConfig.color}
            size={size}
            icon={confidenceConfig.icon as any}
            onClick={handleConfidenceClick}
            sx={{ cursor: 'help', fontWeight: 600 }}
          />
        </Stack>

        {/* Combined Interpretation */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {displayInterpretation}
          </Typography>
        </Box>

        {/* Readiness Popover */}
        <Popover
          open={Boolean(readinessAnchorEl)}
          anchorEl={readinessAnchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              SEO Readiness
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {readinessReason || readinessConfig.description}
            </Typography>
            {readinessEvidenceLink && (
              <Button
                component={RouterLink}
                to={readinessEvidenceLink}
                variant="text"
                size="small"
              >
                View Evidence →
              </Button>
            )}
          </Box>
        </Popover>

        {/* Confidence Popover */}
        <Popover
          open={Boolean(confidenceAnchorEl)}
          anchorEl={confidenceAnchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Statistical Confidence
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {confidenceReason || confidenceConfig.description}
            </Typography>
            {confidenceEvidenceLink && (
              <Button
                component={RouterLink}
                to={confidenceEvidenceLink}
                variant="text"
                size="small"
              >
                View Evidence →
              </Button>
            )}
          </Box>
        </Popover>
      </CardContent>
    </Card>
  );
}
